import React from "react";
import {StackedBarChart} from "../../components";
import {
    COLOR_AMT_TO_BENEFICIARIES_DECREASE,
    COLOR_AMT_TO_BENEFICIARIES_INCREASE,
    COLOR_ENABLED_WPO_STRATEGY,
    COLOR_EST_ESTATE_TAX_DECREASE,
    COLOR_EST_ESTATE_TAX_INCREASE,
    COLOR_NT_GREY,
    COLOR_TAXES
} from "../../constants/colors";
import {StackedBarChartItemType} from "../../components/StackedBarChart/StackedBarChartItem";
import {roundUpToNext, truncateCurrency} from "../../utils/format";
import _ from "lodash";
import ChartStatusLine from "../../ClientManagement/AssetReliance/ChartStatusLine";

export interface AmountToBeneficiariesWithoutStrategiesChartItems {
    data: StackedBarChartItemType[];
}

export interface EstEstateTaxWithoutStrategiesChartItems {
    data: StackedBarChartItemType[];
}

export interface AmountToBeneficiariesWithStrategiesChartItems {
    data: StackedBarChartItemType[];
}

export interface EstEstateTaxWithStrategiesChartItems {
    data: StackedBarChartItemType[];
}

export type FutureEstateValueBarChartsProps = {
    amountToBeneficiariesWithoutStrategies: number,
    estEstateTaxWithoutStrategies: number,
    amountToBeneficiariesWithStrategies: number,
    estEstateTaxWithStrategies: number,
}

export type WPOBarChartUnderlayProps = {
    maxValue: number,
}

const WPOBarChartUnderlay: React.FC<WPOBarChartUnderlayProps> = ({
                                                                     maxValue
                                                                 }: WPOBarChartUnderlayProps) => {
    return (

        <div className="wpo-bar-chart-container">
            {_.range(4).map((index) => {
                const verticalOffset = `${(index * (100 / 3))}`;
                const valueForLabel = maxValue * ((3 - index) * (100 / 300));
                const truncatedValueAsString = valueForLabel !== 0 ? truncateCurrency(valueForLabel, {
                    formatAsCurrency: false,
                    includeUnits: true,
                    maxValue: maxValue
                }) : '';
                return (
                    <div key={index}>
                        <ChartStatusLine
                            id={'barChartUnderlay'}
                            label={`${truncatedValueAsString}`}
                            height={'1px'}
                            zIndex={0}
                            width={`calc(100%)`}
                            lineColor={COLOR_NT_GREY}
                            verticalOffset={verticalOffset}
                            marginLeft={40}
                            textColor={'#989898'}
                            isAssetReliance={false}
                        />
                    </div>
                )
            })}
        </div>
    )
}

const FutureEstateValueBarCharts: React.FC<FutureEstateValueBarChartsProps> = ({
                                                                                   amountToBeneficiariesWithoutStrategies,
                                                                                   estEstateTaxWithoutStrategies,
                                                                                   amountToBeneficiariesWithStrategies,
                                                                                   estEstateTaxWithStrategies
                                                                               }: FutureEstateValueBarChartsProps) => {
    // Grey Scale Bar Chart changes will be included with Gift to Grantor Trust changes
    const enableGreyScaleBarCharts = true;

    const {
        amountToBeneficiariesWithoutStrategiesChartItems,
        estEstateTaxWithoutStrategiesData,
        amountToBeneficiariesWithStrategiesChartItems,
        estEstateTaxWithStrategiesData
    } = getChartItemsData(amountToBeneficiariesWithoutStrategies, estEstateTaxWithoutStrategies, amountToBeneficiariesWithStrategies, estEstateTaxWithStrategies);

    let maxValue = Math.max(amountToBeneficiariesWithoutStrategies, estEstateTaxWithoutStrategies, amountToBeneficiariesWithStrategies, estEstateTaxWithStrategies);
    maxValue = (maxValue === 0) ? 152 : maxValue; // required to show minimum height for bar charts when all amounts are 0

    const chartUnderlayMaxValue = maxValue < 1_000_000 ? 1_000_000 : roundUpToNext(maxValue, 3);

    const columnHeightPercentage = (maxValue / chartUnderlayMaxValue * 100).toFixed(2);

    return (
        <div className={"future-estate-value-bar-charts"}>
            {enableGreyScaleBarCharts && <WPOBarChartUnderlay maxValue={chartUnderlayMaxValue}/>}
            <div className={"strategies"} style={{height: `${enableGreyScaleBarCharts ? columnHeightPercentage : 100}%`}}>
                <StackedBarChart
                    className={"amount-to-beneficiaries-bar-chart"}
                    showLabels={true}
                    data={amountToBeneficiariesWithoutStrategiesChartItems.data}
                />
                <StackedBarChart
                    className={"est-estate-tax-bar-chart"}
                    showLabels={true}
                    data={estEstateTaxWithoutStrategiesData.data}
                />
                <StackedBarChart
                    className={"amount-to-beneficiaries-bar-chart"}
                    showLabels={true}
                    data={amountToBeneficiariesWithStrategiesChartItems.data}
                />
                <StackedBarChart
                    className={"est-estate-tax-bar-chart"}
                    showLabels={true}
                    data={estEstateTaxWithStrategiesData.data}
                />
            </div>
        </div>
    )
};

export const getChartItemsData = (amountToBeneficiariesWithoutStrategies: number,
                                  estEstateTaxWithoutStrategies: number,
                                  amountToBeneficiariesWithStrategies: number,
                                  estEstateTaxWithStrategies: number):
    {
        amountToBeneficiariesWithoutStrategiesChartItems: AmountToBeneficiariesWithoutStrategiesChartItems,
        estEstateTaxWithoutStrategiesData: AmountToBeneficiariesWithStrategiesChartItems,
        amountToBeneficiariesWithStrategiesChartItems: EstEstateTaxWithoutStrategiesChartItems,
        estEstateTaxWithStrategiesData: EstEstateTaxWithStrategiesChartItems
    } => {

    let maxValue = Math.max(amountToBeneficiariesWithoutStrategies, estEstateTaxWithoutStrategies, amountToBeneficiariesWithStrategies, estEstateTaxWithStrategies);
    maxValue = (maxValue === 0) ? 152 : maxValue; // required to show minimum height for bar charts when all amounts are 0

    const isTaxDecreasing = estEstateTaxWithStrategies < estEstateTaxWithoutStrategies;
    const isBeneficiaryAmountDecreasing = amountToBeneficiariesWithStrategies < amountToBeneficiariesWithoutStrategies;

    const {
        withoutStrategiesData: amtToBeneficiariesWithoutStrategiesData,
        withStrategiesData: amtToBeneficiariesWithStrategiesData
    } = isBeneficiaryAmountDecreasing
        ? getChartItemsForDecreasedFutureEstateValue(
            amountToBeneficiariesWithoutStrategies,
            amountToBeneficiariesWithStrategies,
            COLOR_ENABLED_WPO_STRATEGY,
            COLOR_AMT_TO_BENEFICIARIES_DECREASE,
            maxValue)
        : getChartItemsForIncreasedFutureEstateValue(
            amountToBeneficiariesWithoutStrategies,
            amountToBeneficiariesWithStrategies,
            COLOR_ENABLED_WPO_STRATEGY,
            COLOR_AMT_TO_BENEFICIARIES_INCREASE,
            maxValue)

    const {
        withoutStrategiesData: estEstateTaxWithoutStrategiesData,
        withStrategiesData: estEstateTaxWithStrategiesData
    } = isTaxDecreasing
        ? getChartItemsForDecreasedFutureEstateValue(
            estEstateTaxWithoutStrategies,
            estEstateTaxWithStrategies,
            COLOR_TAXES,
            COLOR_EST_ESTATE_TAX_DECREASE,
            maxValue)
        : getChartItemsForIncreasedFutureEstateValue(
            estEstateTaxWithoutStrategies,
            estEstateTaxWithStrategies,
            COLOR_TAXES,
            COLOR_EST_ESTATE_TAX_INCREASE,
            maxValue)

    return {
        amountToBeneficiariesWithoutStrategiesChartItems: {
            data: amtToBeneficiariesWithoutStrategiesData
        } as AmountToBeneficiariesWithoutStrategiesChartItems,

        estEstateTaxWithoutStrategiesData: {
            data: estEstateTaxWithoutStrategiesData
        } as EstEstateTaxWithoutStrategiesChartItems,

        amountToBeneficiariesWithStrategiesChartItems: {
            data: amtToBeneficiariesWithStrategiesData
        } as AmountToBeneficiariesWithStrategiesChartItems,

        estEstateTaxWithStrategiesData: {
            data: estEstateTaxWithStrategiesData
        } as EstEstateTaxWithStrategiesChartItems
    }
}

const getChartItemsForDecreasedFutureEstateValue = (amountWithoutStrategies: number,
                                                    amountWithStrategies: number,
                                                    chartItemColor: string,
                                                    highlightColor: string,
                                                    maxValue: number) => {
    const strategyAmountDifference = Math.abs(amountWithoutStrategies - amountWithStrategies);
    const totalForWhiteSpace = maxValue - amountWithoutStrategies;

    const withoutStrategiesData = [{
        key: `${totalForWhiteSpace}:none`,
        total: totalForWhiteSpace,
        color: "none",
    }, {
        key: `${strategyAmountDifference}:${chartItemColor}`,
        total: strategyAmountDifference,
        color: chartItemColor,
        combinedLabelTotal: 0,
    }, {
        key: `${amountWithStrategies}:${chartItemColor}`,
        label: amountWithoutStrategies > 0 ? truncateCurrency(amountWithoutStrategies) : "$0",
        total: getTotalAmountWithMinHeight(amountWithStrategies, maxValue),
        color: chartItemColor,
        combinedLabelTotal: amountWithoutStrategies,
    }] as StackedBarChartItemType[];

    const withStrategiesData = [{
        key: `${totalForWhiteSpace}:none`,
        total: totalForWhiteSpace,
        color: "none",
    }, {
        key: `${strategyAmountDifference}:${highlightColor}`,
        total: strategyAmountDifference,
        color: highlightColor,
        highlight: strategyAmountDifference,
        combinedLabelTotal: undefined,
    }, {
        key: `${amountWithStrategies}:${highlightColor}`,
        label: amountWithStrategies > 0 ? truncateCurrency(amountWithStrategies) : "$0",
        total: getTotalAmountWithMinHeight(amountWithStrategies, maxValue),
        color: chartItemColor,
        combinedLabelTotal: undefined,
    }] as StackedBarChartItemType[];

    return {withoutStrategiesData, withStrategiesData};
};

const getChartItemsForIncreasedFutureEstateValue = (amountWithoutStrategies: number,
                                                    amountWithStrategies: number,
                                                    chartItemColor: string,
                                                    highlightColor: string,
                                                    maxValue: number) => {
    const strategyAmountDifference = Math.abs(amountWithoutStrategies - amountWithStrategies);
    const totalForWhiteSpace = maxValue - amountWithStrategies;

    const withoutStrategiesData = [{
        key: `${totalForWhiteSpace}:none`,
        total: totalForWhiteSpace,
        color: "none",
    }, {
        key: `${strategyAmountDifference}:${chartItemColor}`,
        total: strategyAmountDifference,
        color: "none",
        combinedLabelTotal: undefined,
    }, {
        key: `${amountWithStrategies}:${chartItemColor}`,
        label: amountWithoutStrategies > 0 ? truncateCurrency(amountWithoutStrategies) : "$0",
        total: getTotalAmountWithMinHeight(amountWithoutStrategies, maxValue),
        color: chartItemColor,
        combinedLabelTotal: undefined,
    }] as StackedBarChartItemType[];

    const withStrategiesData = [{
        key: `${totalForWhiteSpace}:none`,
        total: totalForWhiteSpace,
        color: "none",
    }, {
        key: `${strategyAmountDifference}:${highlightColor}`,
        total: strategyAmountDifference,
        color: highlightColor,
        highlight: strategyAmountDifference,
        combinedLabelTotal: 0,
    }, {
        key: `${amountWithStrategies}:${chartItemColor}`,
        label: amountWithStrategies > 0 ? truncateCurrency(amountWithStrategies) : "$0",
        total: getTotalAmountWithMinHeight(amountWithoutStrategies, maxValue),
        color: chartItemColor,
        combinedLabelTotal: amountWithStrategies,
    }] as StackedBarChartItemType[];

    return {withoutStrategiesData, withStrategiesData};
}

// If the amount is less than or equal to 2.65% of maxValue, this function will return the amount to show minimum height on bar chart
const getTotalAmountWithMinHeight = (amount: number, maxValue: number) => {
    return (amount <= (maxValue * 0.0265)) ? (maxValue * 0.0265) : amount;
};

export default FutureEstateValueBarCharts;
